import { useCallback } from 'react'

import { useQuery, useMutation } from '@apollo/client/react'

import {
  useCurrentOrganization,
  useHasOrganizationAction,
  useOrganizationLimits,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const GetTags = graphql(/* GraphQL */ `
  query GetTags($input: TagsInput!) {
    tags(input: $input) {
      color
      id
      isLocked
      name
    }
  }
`)

export type Tag = ResultOf<typeof GetTags>['tags'][0]

type TagId = Tag['id']

/**
 * Binary search to find the correct insertion index for a tag in alphabetical order.
 * Uses localeCompare for consistent Unicode sorting that matches backend behavior.
 * @param tags - Sorted array of tags
 * @param tagName - Name of the tag to insert
 * @returns Index where the tag should be inserted
 */
function findInsertionIndex(tags: Tag[], tagName: string): number {
  let left = 0
  let right = tags.length

  while (left < right) {
    const mid = Math.floor((left + right) / 2)
    const comparison = tags[mid].name.localeCompare(tagName, undefined, {
      sensitivity: 'base', // Case-insensitive
      numeric: true, // Handle numbers properly
      ignorePunctuation: false, // Keep punctuation for consistent sorting
    })

    if (comparison < 0) {
      left = mid + 1
    } else {
      right = mid
    }
  }

  return left
}

/**
 * Insert a tag into a sorted array at the correct alphabetical position.
 * @param tags - Sorted array of tags
 * @param newTag - Tag to insert
 * @returns New array with tag inserted in correct position
 */
function insertTagAlphabetically(tags: Tag[], newTag: Tag): Tag[] {
  const insertIndex = findInsertionIndex(tags, newTag.name)
  const newTags = [...tags]
  newTags.splice(insertIndex, 0, newTag)
  return newTags
}

export const CreateTag = graphql(/* GraphQL */ `
  mutation CreateTag($input: CreateTagInput!) {
    createTag(input: $input) {
      ... on TagActionSuccess {
        tag {
          id
          name
          color
          isLocked
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

export const UpdateTag = graphql(/* GraphQL */ `
  mutation UpdateTag($input: UpdateTagInput!) {
    updateTag(input: $input) {
      ... on TagActionSuccess {
        tag {
          id
          name
          color
          isLocked
        }
      }
      ... on MutationError {
        message
      }
    }
  }
`)

export const DeleteTag = graphql(/* GraphQL */ `
  mutation DeleteTag($input: DeleteTagInput!) {
    deleteTag(input: $input) {
      ... on DeleteTagActionSuccess {
        id
      }
      ... on MutationError {
        message
      }
    }
  }
`)

export type CreateTag = (tag: { name: string; color: string }) => Promise<Tag>
export type DeleteTag = (id: TagId) => Promise<TagId>
export type UpdateTag = (tag: Omit<Tag, 'isLocked'>) => Promise<Tag>
export type UseTagsQueryReturn = {
  tags: Tag[]
  error: string | null
  loading: boolean
}

export type UseTagsReturn = UseTagsQueryReturn & {
  hasCreatePermission: boolean
  createTag: CreateTag
  updateTag: UpdateTag
  deleteTag: DeleteTag
}

/**
 * Hook to manage tags including fetching, creating, updating, and deleting tags.
 * @returns {UseTagsReturn} The tags, loading state, error state, and functions for CRUD operations on tags.
 * @example
 * const { tags, error, loading, createTag, updateTag, deleteTag, hasCreatePermission } = useTags();
 *
 * useEffect(() => {
 *   if (!loading && !error) {
 *     console.log(tags);
 *   }
 * }, [tags, loading, error]);
 *
 * const handleCreateTag = async () => {
 *   try {
 *     const newTagId = await createTag({ name: 'New Tag', color: '#ff0000' });
 *     console.log('Tag created with ID:', newTagId);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 *
 * const handleUpdateTag = async (tag) => {
 *   try {
 *     const updatedTag = await updateTag(tag);
 *     console.log('Tag updated:', updatedTag);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 *
 * const handleDeleteTag = async (id) => {
 *   try {
 *     const deletedTagId = await deleteTag(id);
 *     console.log('Tag deleted with ID:', deletedTagId);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useTags(): UseTagsReturn {
  // Tag mutations are gated on the organization `edit` action server-side.
  const canManageTags = useHasOrganizationAction('manageTags')

  const { tags, error, loading } = useTagsQuery()
  const { tags: tagsLimit } = useOrganizationLimits() ?? {}

  const hasCreatePermission = Boolean(
    tagsLimit && canManageTags && tags.length < tagsLimit,
  )

  const createTag = useCreateTag()
  const deleteTag = useDeleteTag()
  const updateTag = useUpdateTag()

  return {
    tags,
    error,
    loading,
    createTag,
    deleteTag,
    updateTag,
    hasCreatePermission,
  }
}

const empty: Tag[] = []
/**
 * Hook to fetch tags for the current organization.
 * @returns {UseTagsQueryReturn} The tags, loading state, and error state.
 * @example
 * const { tags, error, loading } = useTagsQuery();
 *
 * useEffect(() => {
 *   if (!loading && !error) {
 *     console.log(tags);
 *   }
 * }, [tags, loading, error]);
 */
export function useTagsQuery(): UseTagsQueryReturn {
  const organizationId = useCurrentOrganization()?.id
  const { loading, data, dataState, error } = useQuery(GetTags, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - organizationId may be undefined query is skipped if it is
      input: { organizationId },
    },
    fetchPolicy: 'cache-first',
    skip: !organizationId,
  })

  if (loading || !organizationId)
    return { tags: empty, error: null, loading: true }

  return {
    tags: dataState === 'complete' ? data.tags : empty,
    error: error ? 'Error loading tags' : null,
    loading,
  }
}

/**
 * Hook to create a new tag with optimistic updates and alphabetical ordering.
 * The tag is immediately added to the UI in the correct alphabetical position,
 * and automatically reverted if the mutation fails.
 * @returns {CreateTag} Function to create a new tag.
 * @example
 * const createTag = useCreateTag();
 *
 * const handleCreateTag = async () => {
 *   try {
 *     const newTag = await createTag({ name: 'New Tag', color: '#ff0000' });
 *     console.log('Tag created with ID:', newTag.id);
 *   } catch (err) {
 *     // Optimistic update is automatically reverted by Apollo Client
 *     // Handle error appropriately (show toast, etc.)
 *   }
 * };
 */
export function useCreateTag(): CreateTag {
  const organizationId = useCurrentOrganization()?.id

  const [createTagMutation] = useMutation(CreateTag, {
    refetchQueries: ['GetTags'],
  })

  const createTag = useCallback(
    async ({ name, color }: { name: string; color: string }): Promise<Tag> => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      // Generate temporary ID for optimistic update
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const optimisticTag: Tag = {
        id: tempId,
        name,
        color,
        isLocked: false,
      }

      const { data, error } = await createTagMutation({
        variables: {
          input: {
            organizationId,
            tag: { name, color },
          },
        },
        optimisticResponse: {
          createTag: {
            __typename: 'TagActionSuccess',
            tag: optimisticTag,
          },
        },
        update: (cache, { data: updateData }) => {
          if (
            updateData?.createTag?.__typename === 'TagActionSuccess' &&
            organizationId
          ) {
            const newTag = updateData.createTag.tag
            const existingTags = cache.readQuery({
              query: GetTags,
              variables: { input: { organizationId } },
            })

            if (existingTags) {
              // If this is the optimistic response, add the tag in alphabetical order
              // If server response, replace optimistic tag with real tag (maintaining position)
              const isOptimisticUpdate = newTag.id === tempId
              let updatedTags: Tag[]

              if (isOptimisticUpdate) {
                // Insert new tag in correct alphabetical position
                updatedTags = insertTagAlphabetically(existingTags.tags, newTag)
              } else {
                // Replace temporary tag with real tag from server
                updatedTags = existingTags.tags.map((tag: Tag) =>
                  tag.id === tempId ? newTag : tag,
                )
              }

              cache.writeQuery({
                query: GetTags,
                variables: { input: { organizationId } },
                data: {
                  tags: updatedTags,
                },
              })
            }
          }
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to create tag')
      }

      const result = data.createTag
      if (result.__typename !== 'TagActionSuccess') {
        throw new Error(
          'message' in result ? result.message : 'Failed to create tag',
        )
      }

      return result.tag
    },
    [createTagMutation, organizationId],
  )

  return createTag
}

/**
 * Hook to delete a tag.
 * @returns {DeleteTag} Function to delete a tag.
 * @example
 * const deleteTag = useDeleteTag();
 *
 * const handleDeleteTag = async (id) => {
 *   try {
 *     const deletedTagId = await deleteTag(id);
 *     console.log('Tag deleted with ID:', deletedTagId);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export function useDeleteTag(): DeleteTag {
  const organizationId = useCurrentOrganization()?.id

  const [deleteTagMutation] = useMutation(DeleteTag, {
    refetchQueries: ['GetTags'],
  })

  const deleteTag = useCallback(
    async (id: TagId): Promise<TagId> => {
      const { data, error } = await deleteTagMutation({
        variables: {
          input: {
            id,
          },
        },
        optimisticResponse: {
          deleteTag: {
            __typename: 'DeleteTagActionSuccess',
            id,
          },
        },
        update: (cache, { data: updateData }) => {
          if (
            updateData?.deleteTag?.__typename === 'DeleteTagActionSuccess' &&
            organizationId
          ) {
            const existingTags = cache.readQuery({
              query: GetTags,
              variables: { input: { organizationId } },
            })

            if (existingTags) {
              cache.writeQuery({
                query: GetTags,
                variables: { input: { organizationId } },
                data: {
                  tags: existingTags.tags.filter((t: Tag) => t.id !== id),
                },
              })
            }
          }
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to delete tag')
      }

      const result = data.deleteTag
      if (result.__typename !== 'DeleteTagActionSuccess') {
        throw new Error(
          'message' in result ? result.message : 'Failed to delete tag',
        )
      }

      return result.id
    },
    [deleteTagMutation, organizationId],
  )

  return deleteTag
}

/**
 * Hook to update a tag with optimistic updates and alphabetical reordering.
 * If the tag name changes, it's automatically repositioned in alphabetical order.
 * Updates are reverted if the mutation fails.
 * @returns {UpdateTag} Function to update a tag.
 * @example
 * const updateTag = useUpdateTag();
 *
 * const handleUpdateTag = async (tag) => {
 *   try {
 *     const updatedTag = await updateTag(tag);
 *     console.log('Tag updated:', updatedTag);
 *   } catch (err) {
 *     // Optimistic update is automatically reverted by Apollo Client
 *     // Handle error appropriately (show toast, etc.)
 *   }
 * };
 */
export function useUpdateTag(): UpdateTag {
  const organizationId = useCurrentOrganization()?.id

  const [updateTagMutation] = useMutation(UpdateTag, {
    refetchQueries: ['GetTags'],
  })

  const updateTag = useCallback(
    async (tag: Omit<Tag, 'isLocked'>): Promise<Tag> => {
      const { data, error } = await updateTagMutation({
        variables: {
          input: {
            id: tag.id,
            tag: {
              name: tag.name,
              color: tag.color,
            },
          },
        },
        optimisticResponse: {
          updateTag: {
            __typename: 'TagActionSuccess',
            tag: {
              ...tag,
              // TODO: we should not need to set this here
              isLocked: false,
            },
          },
        },
        update: (cache, { data: updateData }) => {
          if (
            updateData?.updateTag?.__typename === 'TagActionSuccess' &&
            organizationId
          ) {
            const updatedTag = updateData.updateTag.tag
            const existingTags = cache.readQuery({
              query: GetTags,
              variables: { input: { organizationId } },
            })

            if (existingTags) {
              const currentTag = existingTags.tags.find(
                (t: Tag) => t.id === updatedTag.id,
              )

              // If name changed, we need to reposition the tag alphabetically
              if (currentTag && currentTag.name !== updatedTag.name) {
                // Remove the old tag and insert the updated one in correct position
                const tagsWithoutOld = existingTags.tags.filter(
                  (t: Tag) => t.id !== updatedTag.id,
                )
                const updatedTags = insertTagAlphabetically(
                  tagsWithoutOld,
                  updatedTag,
                )

                cache.writeQuery({
                  query: GetTags,
                  variables: { input: { organizationId } },
                  data: {
                    tags: updatedTags,
                  },
                })
              } else {
                // Name didn't change, just update in place
                cache.writeQuery({
                  query: GetTags,
                  variables: { input: { organizationId } },
                  data: {
                    tags: existingTags.tags.map((t: Tag) =>
                      t.id === updatedTag.id ? updatedTag : t,
                    ),
                  },
                })
              }
            }
          }
        },
      })

      if (error || !data) {
        throw new Error(error?.message ?? 'Failed to update tag')
      }

      const result = data.updateTag
      if (result.__typename !== 'TagActionSuccess') {
        throw new Error(
          'message' in result ? result.message : 'Failed to update tag',
        )
      }

      return result.tag
    },
    [updateTagMutation, organizationId],
  )

  return updateTag
}
